import { useMemo } from "react";
import dayjs from "dayjs";
import { 
    generateGradientMetaMap, GREEN_GRADIENT, BLUE_GRADIENT, 
    ORANGE_GRADIENT, PURPLE_GRADIENT, YELLOW_GRADIENT, 
    CYAN_GRADIENT, transformTimelineForType 
} from "../../../utils/cameraUtils";

export const useCameraAnalytics = (analytics, camera, t, minPercentage) => {
  const autoServiceClassIdToMeta = useMemo(() => ({
    "-1": { label: t("classes.downtime"), color: "#6c757d" },
    "-2": { label: t("classes.idle_vehicle"), color: "#ffc107" },
    "-101": { label: t("classes.vehicle_w_people", { count: 1 }), color: "#d1e7dd" },
    "-102": { label: t("classes.vehicle_w_people", { count: 2 }), color: "#afdac0" },
    "-103": { label: t("classes.vehicle_w_people", { count: 3 }), color: "#8dcd9f" },
    "-104": { label: t("classes.vehicle_w_people", { count: 4 }), color: "#6bbf7e" },
    "-105": { label: t("classes.vehicle_w_people", { count: 5 }), color: "#4ab25e" },
    "-106": { label: t("classes.vehicle_w_people", { count: 6 }), color: "#389e4c" },
    "-107": { label: t("classes.vehicle_w_people", { count: 7 }), color: "#2a8b3d" },
    "-108": { label: t("classes.vehicle_w_people", { count: 8 }), color: "#1d782e" },
    "-109": { label: t("classes.vehicle_w_people", { count: 9 }), color: "#106520" },
    "-110": { label: t("classes.vehicle_w_people", { count: 10 }), color: "#035211" },
    "-111": { label: t("classes.vehicle_w_people", { count: "10+" }), color: "#00400c" },
    default: { label: t("classes.unknown"), color: "#ccc" },
  }), [t]);

  const personnelGeneralMeta = useMemo(() => generateGradientMetaMap(-100, -1, GREEN_GRADIENT, t("camera.presence_state"), t("classes.downtime_no_people")), [t]);
  const chairMeta = useMemo(() => { const meta = generateGradientMetaMap(-300, -30, BLUE_GRADIENT, t("camera.chair_state"), t("camera.no_people_state")); meta["-31"] = { label: t("camera.chair_state"), color: "#3399ff" }; return meta; }, [t]);
  const phoneMeta = useMemo(() => { const meta = generateGradientMetaMap(-400, -40, ORANGE_GRADIENT, t("camera.phone_state"), t("camera.no_people_state")); meta["-41"] = { label: t("camera.phone_state"), color: "#ff9933" }; return meta; }, [t]);
  const queueMeta = useMemo(() => { const meta = generateGradientMetaMap(-500, -50, PURPLE_GRADIENT, t("camera.queue_state", "В очереди"), t("camera.no_people_state")); meta["-51"] = { label: t("camera.queue_state", "В очереди"), color: "#9c27b0" }; return meta; }, [t]);
  const machineMeta = useMemo(() => { const meta = generateGradientMetaMap(-600, -60, YELLOW_GRADIENT, t("camera.machine_state", "Станок работает"), t("camera.no_people_state")); meta["-61"] = { label: t("camera.machine_state", "Станок работает"), color: "#ffb300" }; return meta; }, [t]);
  const managerMeta = useMemo(() => { const meta = generateGradientMetaMap(-700, -70, CYAN_GRADIENT, t("camera.manager_state", "Общение с клиентом"), t("camera.no_people_state")); meta["-71"] = { label: t("camera.manager_state", "Общение с клиентом"), color: "#00bcd4" }; return meta; }, [t]);

  const dailySummary = useMemo(() => {
    // РАЗРЕШАЕМ И ДЛЯ CAFE, И ДЛЯ MANAGER
    if (!analytics || !["cafe", "manager"].includes(analytics.mode)) return [];
    
    const getCameraLocalFromTimeline = (backendIsoString) => {
      const d = new Date(backendIsoString);
      return dayjs(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours() + 3, d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds()));
    };
    const days = {};
    const processTimeline = (timeline, isQueue) => {
      timeline.forEach((seg) => {
        if (seg.people_count === 0) return;
        const startLocal = getCameraLocalFromTimeline(seg.start);
        const endLocal = getCameraLocalFromTimeline(seg.end);
        const dateStr = startLocal.format("YYYY-MM-DD");
        if (!days[dateStr]) days[dateStr] = { date: dateStr };
        if (isQueue) {
          if (!days[dateStr].firstClient || startLocal.isBefore(days[dateStr].firstClient)) days[dateStr].firstClient = startLocal;
          if (!days[dateStr].lastClient || endLocal.isAfter(days[dateStr].lastClient)) days[dateStr].lastClient = endLocal;
        } else {
          if (!days[dateStr].pointOpen || startLocal.isBefore(days[dateStr].pointOpen)) days[dateStr].pointOpen = startLocal;
          if (!days[dateStr].pointClose || endLocal.isAfter(days[dateStr].pointClose)) days[dateStr].pointClose = endLocal;
        }
      });
    };
    processTimeline(analytics.general?.timeline || [], false);
    processTimeline(analytics.queue?.timeline || [], true);
    return Object.values(days).sort((a, b) => a.date.localeCompare(b.date));
  }, [analytics]);

  const groupedVehicleLogs = useMemo(() => {
    if (camera?.operating_mode !== "auto_service" || !analytics?.vehicle_logs?.length) return [];
    const logsById = new Map(analytics.vehicle_logs.map((log) => [log.id, log]));
    const commentsMap = new Map(analytics.vehicle_analysis_comments.map((c) => [c.first_vehicle_log_id, c]));
    const sortedLogIds = analytics.vehicle_logs.map((l) => l.id).sort((a, b) => dayjs(logsById.get(a).entry_time).diff(dayjs(logsById.get(b).entry_time)));
    const groups = new Map();
    let currentGroupId = 1;

    for (let i = 0; i < sortedLogIds.length; i++) {
      const logId = sortedLogIds[i];
      if (i === 0) { groups.set(currentGroupId, [logId]); continue; }
      const comparisonComment = commentsMap.get(sortedLogIds[i - 1]);
      if (comparisonComment && comparisonComment.is_same_vehicle) groups.get(currentGroupId).push(logId);
      else { currentGroupId++; groups.set(currentGroupId, [logId]); }
    }
    return Array.from(groups.entries()).map(([id, logIds]) => {
      const logs = logIds.map((lId) => logsById.get(lId));
      return { id, logs, startTime: logs[0].entry_time, endTime: logs[logs.length - 1].exit_time, total_man_hours: logs.reduce((sum, l) => sum + (l.total_man_hours || 0), 0) };
    }).sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
  }, [analytics, camera]);

  const dynamicSummary = useMemo(() => {
    if (!analytics) return null;
    let totalWorkManSeconds = 0, filteredWorkDuration = 0, totalWorkDuration = 0, totalDowntime = 0, totalChairManSeconds = 0, totalPhoneManSeconds = 0, maxPeople = 0;
    
    let totalMachineWork = 0;
    let totalMachineIdle = 0;
    let machineEfficiency = 0;
    let totalManagerManSeconds = 0;
    let durationsList = [];

    const sumManSeconds = (list) => list?.reduce((acc, item) => acc + item.people_count * item.total_duration_seconds, 0) || 0;
    
    if (["personnel_control", "cafe", "szsm", "manager"].includes(analytics.mode)) {
      totalDowntime = analytics.general?.total_downtime_seconds || 0;
      totalChairManSeconds = sumManSeconds(analytics.chair?.durations_by_people_count);
      totalPhoneManSeconds = sumManSeconds(analytics.phone?.durations_by_people_count);
      durationsList = analytics.general?.durations_by_people_count || [];

      if (analytics.mode === "szsm" && analytics.machine) {
          totalMachineWork = analytics.machine.durations_by_people_count?.reduce((acc, item) => acc + item.total_duration_seconds, 0) || 0;
          totalMachineIdle = analytics.machine.total_downtime_seconds || 0;
          const totalMachineTime = totalMachineWork + totalMachineIdle;
          if (totalMachineTime > 0) {
              machineEfficiency = (totalMachineWork / totalMachineTime) * 100;
          }
      }
      
      if (analytics.mode === "manager" && analytics.manager) {
          totalManagerManSeconds = sumManSeconds(analytics.manager?.durations_by_people_count);
      }

    } else {
      const pureDowntime = analytics.general_stats?.total_downtime_seconds || 0;
      const idleTime = analytics.general_stats?.total_idle_time_seconds || 0;
      totalDowntime = pureDowntime + idleTime;
      totalChairManSeconds = sumManSeconds(analytics.chair_stats?.durations_by_people_count);
      totalPhoneManSeconds = sumManSeconds(analytics.phone_stats?.durations_by_people_count);
      durationsList = analytics.general_stats?.durations_by_people_count || [];
    }

    totalWorkDuration = durationsList.reduce((acc, item) => acc + item.total_duration_seconds, 0);
    if (totalWorkDuration > 0) {
      [...durationsList].sort((a, b) => b.people_count - a.people_count).forEach((item) => {
        if ((item.total_duration_seconds / totalWorkDuration) * 100 >= minPercentage) {
          totalWorkManSeconds += item.people_count * item.total_duration_seconds;
          filteredWorkDuration += item.total_duration_seconds;
          if (maxPeople === 0) maxPeople = item.people_count;
        }
      });
    }

    let efficiency = totalWorkManSeconds > 0 ? (Math.max(0, totalWorkManSeconds - (totalChairManSeconds + totalPhoneManSeconds)) / totalWorkManSeconds) * 100 : 0;
    let presenceEfficiency = (totalWorkManSeconds + totalDowntime) > 0 ? (totalWorkManSeconds / (totalWorkManSeconds + totalDowntime)) * 100 : 0;
    let weightedAveragePeople = filteredWorkDuration > 0 ? totalWorkManSeconds / filteredWorkDuration : 0;

    return { 
        maxPeople, weightedAveragePeople, totalDowntime, 
        totalWork: totalWorkManSeconds, totalChair: totalChairManSeconds, totalPhone: totalPhoneManSeconds, 
        efficiency, presenceEfficiency,
        totalMachineWork, totalMachineIdle, machineEfficiency,
        totalManager: totalManagerManSeconds
    };
  }, [analytics, minPercentage]);

  const chairTimelineToShow = useMemo(() => !analytics ? [] : ["personnel_control", "cafe", "szsm", "manager"].includes(analytics.mode) ? analytics.chair?.timeline || [] : transformTimelineForType(analytics.general_stats?.timeline || analytics.timeline, "chair"), [analytics]);
  const phoneTimelineToShow = useMemo(() => !analytics ? [] : ["personnel_control", "cafe", "szsm", "manager"].includes(analytics.mode) ? analytics.phone?.timeline || [] : transformTimelineForType(analytics.general_stats?.timeline || analytics.timeline, "phone"), [analytics]);
  const machineTimelineToShow = useMemo(() => !analytics ? [] : analytics.mode === "szsm" ? analytics.machine?.timeline || [] : [], [analytics]);
  const managerTimelineToShow = useMemo(() => !analytics ? [] : analytics.mode === "manager" ? analytics.manager?.timeline || [] : [], [analytics]);

  return {
    autoServiceClassIdToMeta, personnelGeneralMeta, chairMeta, phoneMeta, queueMeta, machineMeta, managerMeta,
    dailySummary, groupedVehicleLogs, dynamicSummary, chairTimelineToShow, phoneTimelineToShow, machineTimelineToShow, managerTimelineToShow
  };
};